import { css } from '@emotion/css';
import { memo, useState } from 'react';
import InputAccount from './input-account';
import { useAppDispatch, useAppSelector, RootState } from 'cache/store';
import { useForm } from 'react-hook-form';
import userApi from 'apis/userApi';
import { cacheUserInfo } from 'features/authSlice';
import toast from 'react-hot-toast';
import LoadingIcon from 'components/loading/LoadingIcon';
import { PHONE_VERIFY_URL } from 'constants/common';
import { msgAlertInput } from 'constants/messageAlert';

interface FormValuesAccount {
    nickname: string;
    birthDay: string;
}

interface FormValuesAccountPass {
    oldpwd: string;
    newpwd: string;
    newpwd2: string;
    oldcheckpwd: string;
    newcheckpwd: string;
    newcheckpwd2: string;
}
interface PassUpdate {
    order: number;
    value: string;
    key: string;
    valid: boolean;
}

function PersonalInfo() {
    const dispatch = useAppDispatch();
    const { userInfo } = useAppSelector((state: RootState) => state.auth);

    const [nickname, setNickname] = useState(userInfo.nickname);
    const [loading, setLoading] = useState(false);

    const [oldpwd, setOldpwd] = useState('');
    const [newpwd, setNewpwd] = useState('');
    const [newpwd2, setNewpwd2] = useState('');

    const [oldcheckpwd, setOldcheckpwd] = useState('');
    const [newcheckpwd, setNewcheckpwd] = useState('');
    const [newcheckpwd2, setNewcheckpwd2] = useState('');

    const {
        setError,
        formState: { errors },
    } = useForm<FormValuesAccount>();

    const { handleSubmit, setValue } = useForm<FormValuesAccountPass>();

    const submitChangeNickname = async () => {
        if (nickname.trim() === '') {
            setError('nickname', { message: 'Biệt danh không hợp lệ' });
            return;
        }
        if (errors['nickname']) return;

        setLoading(true);
        try {
            const { data } = await userApi.changeNickname({ userNickname: nickname });
            if (!data.msg) {
                dispatch(cacheUserInfo({ ...userInfo, nickname }));
                toast.success('Cập nhật thành công!');
            } else {
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const onEditAccount = (e: 'updatePass' | 'updatePassPay') => {
        const passlogin = [
            { order: 1, value: oldpwd, key: 'oldpwd', valid: false },
            { order: 2, value: newpwd, key: 'newpwd', valid: false },
            { order: 3, value: newpwd2, key: 'newpwd2', valid: false },
        ];
        const passPayment = [
            { order: 1, value: oldcheckpwd, key: 'oldcheckpwd', valid: false },
            { order: 2, value: newcheckpwd, key: 'newcheckpwd', valid: false },
            { order: 3, value: newcheckpwd2, key: 'newcheckpwd2', valid: false },
        ];

        if (e === 'updatePass') {
            submitUpdatePass(passlogin, 'PersonPwdBean_loginPWD');
        }
        if (e === 'updatePassPay') {
            submitUpdatePass(passPayment, 'PersonPwdBean_checkPWD');
        }
    };
    const submitUpdatePass = async (listPass: PassUpdate[], typePass: 'PersonPwdBean_loginPWD' | 'PersonPwdBean_checkPWD') => {
        const isValidPass = checkValidPass(listPass);

        if (!isValidPass) return;

        setLoading(true);
        let passObj: { [key: string]: string } = {};
        listPass.forEach((passItem) => {
            passObj = { ...passObj, [passItem.key]: passItem.value };
        });
        const payload = {
            ...passObj,
            flag: typePass,
        };
        try {
            const { data } = await userApi.editPassWord(payload);
            if (!data.msg) {
                let msgAlert = 'Cập nhật mật khẩu thành công!';
                if (typePass === 'PersonPwdBean_checkPWD') {
                    msgAlert = 'Cập nhật mật khẩu thanh toán thành công!';
                }
                toast.success(msgAlert);
                //reset input
                listPass.forEach((passItem: any) => {
                    setValue(passItem.key, '');
                });
            } else {
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const checkValidPass = (listPass: PassUpdate[]) => {
        listPass = listPass.map((item) => {
            if (!item.value) {
                setError(item.key as any, {
                    message: msgAlertInput.generic.require,
                });
                return { ...item, valid: false };
            } else if (item.value.trim().length < 6) {
                setError(item.key as any, {
                    message: msgAlertInput.generic.minLength(6),
                });
                return { ...item, valid: false };
            } else if (item.value.trim().length > 30) {
                setError(item.key as any, {
                    message: msgAlertInput.generic.maxLength(30),
                });
                return { ...item, valid: false };
            }
            return { ...item, valid: true };
        });

        const isValidValue = listPass.every((item) => item.valid);
        if (!isValidValue) {
            return false;
        }

        const isMatchNewPass = listPass[1].value === listPass[2].value;
        if (!isMatchNewPass) {
            setError(listPass[2].key as any, {
                message: msgAlertInput.prepassword.invalid,
            });
            return false;
        }

        return true;
    };

    return (
        <div className={styles.container}>
            <div className={styles.viewInput}>
                <p className={styles.title}>Biệt danh</p>
                <InputAccount title="Tên đăng nhập:" value={userInfo.loginname} disabled />
                <InputAccount title="Biệt danh:" value={nickname} onChangeText={setNickname} />
                <button onClick={submitChangeNickname} className={styles.buttonSave}>
                    <p>Lưu</p>
                </button>
            </div>

            <div className={styles.viewInput}>
                <p className={styles.title}>Số điện thoại</p>
                <iframe src={PHONE_VERIFY_URL} width="100%" height="400px" style={{ border: '0.5px solid #ccc' }} title="Phone Verification" />
            </div>

            <div className={styles.viewInput}>
                <p className={styles.title}>Mật khẩu</p>
                <InputAccount title="Mật khẩu cũ:" value={oldpwd} onChangeText={setOldpwd} password />
                <InputAccount title="Mật khẩu mới:" value={newpwd} onChangeText={setNewpwd} password />
                <InputAccount title="Nhập lại mật khẩu mới:" value={newpwd2} onChangeText={setNewpwd2} password />
                <button id="updatePass" type="button" onClick={handleSubmit(() => onEditAccount('updatePass'))} className={styles.buttonSave}>
                    <p>Lưu</p>
                </button>
            </div>

            <div className={styles.viewInput}>
                <p className={styles.title}>Mật khẩu thanh toán</p>
                <InputAccount title="Mật khẩu cũ:" value={oldcheckpwd} onChangeText={setOldcheckpwd} />
                <InputAccount title="Mật khẩu mới:" value={newcheckpwd} onChangeText={setNewcheckpwd} />
                <InputAccount title="Nhập lại mật khẩu mới:" value={newcheckpwd2} onChangeText={setNewcheckpwd2} />
                <button id="updatePassPay" type="button" onClick={handleSubmit(() => onEditAccount('updatePassPay'))} className={styles.buttonSave}>
                    <p>Lưu</p>
                </button>
            </div>

            {loading && <LoadingIcon />}
        </div>
    );
}

export default memo(PersonalInfo);

const styles = {
    container: css`
        background-color: white;
        padding-top: 20px;
        padding-bottom: 50px;
        padding-left: 20px;
        padding-right: 20px;
        border-radius: 5px;
        gap: 30px;
        display: flex;
        flex-direction: column;
    `,
    title: css`
        font-size: 24px;
        font-weight: 300;
    `,
    viewInput: css`
        gap: 20px;
        display: flex;
        flex-direction: column;
        width: 100%;
        align-self: center;
    `,
    buttonSave: css`
        background: #ffad33;
        align-self: flex-end;
        margin-right: 150px;
        border: 1px solid #c9820a !important;
        border-radius: 3px !important;
        padding-left: 25px;
        padding-right: 25px;
        padding-top: 5px;
        padding-bottom: 5px;
        cursor: pointer;
        p {
            color: white;
            font-weight: 400;
            font-size: 15px;
        }
    `,
};
